/**
 * The pill at the top of the graph naming the selection: "Logger · Class · Backend" for a
 * node, "Bus uses Logger" for a link, "Modules · Group · 15 nodes" for a group,
 * "Backend → Libraries · 17 links" for a bundle, "3 nodes · Bus, Logger, Graph" for several.
 * A group's pill also folds or unfolds it, and renames it in place: the name becomes a field
 * (`#group-rename`, also opened by a double click on the group's name or F2), sent on Enter or
 * when leaving it, Escape giving up.
 */
import { Show, createEffect, createMemo, type JSX } from "solid-js";
import { useApp } from "../../app/context";
import { nodesInGroup } from "../../shared/analysis";
import { endName } from "../../state/bundles";
import { nameOf } from "./edits";

/** What the pill says: a bold name, a detail, and for a link the other bold name. */
interface PillText {
  a: string;
  middle: string;
  b?: string;
  /** For a group: its id and whether it is folded. */
  group?: { id: string; collapsed: boolean };
}

/** The selection pill (hidden when nothing is selected). */
export function SelectionPill(): JSX.Element {
  const { graph, view, scene, kinds, actions } = useApp();
  const text = createMemo((): PillText | null => {
    void graph.state.version;
    const s = view.state.selection;
    if (s?.type === "link") return { a: nameOf(scene, s.link.from), middle: kinds.ofLink(s.link).verb, b: nameOf(scene, s.link.to) };
    if (s?.type === "node") {
      const n = graph.state.graph.nodes.find((x) => x.id === s.id);
      if (n) return { a: n.name, middle: [kinds.ofNode(n).label, ...n.categories].join(" · ") };
    }
    if (s?.type === "nodes") {
      const names = s.ids.map((id) => graph.state.graph.nodes.find((x) => x.id === id)?.name ?? id);
      return { a: `${names.length} nodes`, middle: names.slice(0, 3).join(", ") + (names.length > 3 ? "…" : "") };
    }
    if (s?.type === "bundle") {
      const b = scene.bundleByKey.get(s.key);
      if (b) return { a: endName(graph.state.graph, b.from), middle: `→`, b: `${endName(graph.state.graph, b.to)} · ${b.links.length} links` };
    }
    if (s?.type === "group") {
      const g = graph.state.graph.groups.find((x) => x.id === s.id);
      const count = nodesInGroup(graph.state.graph, s.id, true).length;
      if (g) return { a: g.name, middle: `${g.collapsed ? "Folded group" : "Group"} · ${count} ${count === 1 ? "node" : "nodes"}`, group: { id: g.id, collapsed: !!g.collapsed } };
    }
    return null;
  });
  let field: HTMLInputElement | undefined;
  /** The group being renamed, while its name is a field. */
  const renaming = () => {
    const t = text();
    return t?.group && view.state.renaming === t.group.id ? t.group.id : null;
  };
  createEffect(() => {
    if (renaming()) queueMicrotask(() => field?.select());
  });
  /** Sends the name written (if it changed) and turns the field back into the pill. */
  const finish = (keep: boolean) => {
    const id = renaming();
    if (!id) return;
    const name = field?.value.trim() ?? "";
    view.renameGroup(null);
    if (keep && name && name !== text()?.a) void actions.updateGroup(id, { name });
  };
  return (
    <Show when={text()}>
      {(t) => (
        <div class="pill" data-pill classList={{ "with-tools": !!t().group }}>
          <Show
            when={renaming()}
            fallback={
              <>
                <b onDblClick={() => t().group && view.renameGroup(t().group!.id)}>{t().a}</b> <span>{t().middle}</span>{" "}
              </>
            }
          >
            <input
              ref={field}
              id="group-rename"
              class="pill-field"
              value={t().a}
              aria-label="Group name"
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Enter") finish(true);
                if (e.key === "Escape") finish(false);
              }}
              onBlur={() => finish(true)}
            />
          </Show>
          <Show when={t().b}>
            <b>{t().b}</b>
          </Show>
          <Show when={t().group && !renaming()}>
            <button type="button" class="pill-tool" data-pill-fold title="Fold the group into one disc, or open it (C)" onClick={() => void actions.updateGroup(t().group!.id, { collapsed: !t().group!.collapsed })}>
              {t().group!.collapsed ? "Unfold" : "Fold"}
            </button>
            <button type="button" class="pill-tool" data-pill-rename title="Rename the group (F2, or double-click its name)" onClick={() => view.renameGroup(t().group!.id)}>
              Rename
            </button>
          </Show>
        </div>
      )}
    </Show>
  );
}
